// application/useCases/changeServiceCodeLifespan.js

import {
  serviceCodeRemovalRejection,
  serviceCodeRetirementRejection,
} from '../../domain/services/serviceCodeGovernance';
import { toAsOfDate } from '../../domain/services/effectiveDating';
import { ServiceCodeChangeRejected } from '../ports/serviceCodeErrors';

/**
 * The two things that can happen to an org-authored code after it exists — and they are not degrees
 * of one thing.
 *
 * RETIRING SAYS THE CODE EXISTED AND STOPPED APPLYING. REMOVING SAYS IT SHOULD NEVER HAVE EXISTED.
 * That is why they gate differently and why one is always available while the other closes forever.
 * A funder that stops accepting a code produces a retirement; a typo produces a removal; and no
 * amount of either turns into the other.
 *
 * BOTH LIVE IN ONE FILE because they are the same decision seen from two sides, they share the
 * lookup and the refusal type, and a caller offering one in a menu offers the other beside it.
 *
 * NEITHER READS A CLOCK. The retirement date arrives from the edge like every other calendar date
 * here, and the removal gate takes its evidence as arguments, so both are decidable from their
 * inputs alone.
 */

/**
 * The code by id, or a refusal that names what is wrong.
 *
 * RE-READ RATHER THAN TRUSTED, and this is the point of routing these through a use case at all. The
 * editor loaded the catalog when it opened; a colleague can retire or remove the same code while
 * the menu is open, and acting on the stale copy would write over a decision nobody saw. The reject
 * functions then run against what storage actually holds.
 */
const findCode = async (serviceCodeId, clinicId, repository) => {
  const available = await repository.loadAvailable({ clinicId });

  return available.find((serviceCode) => serviceCode.id === serviceCodeId) ?? null;
};

/**
 * Closes an org's own code from a date, or reopens one that was closed.
 *
 * `retiredOn` IS THE LAST BILLABLE SERVICE DATE, INCLUSIVE. A code retired 2026-12-31 bills through
 * that day, which is the semantics settled for the catalog and shared here rather than re-decided.
 * Any copy calling it "retired from" is wrong by one day and has to say "last billable" or name the
 * date as the retirement date itself.
 *
 * REVERSIBLE, INCLUDING AFTER THE DATE HAS PASSED. Passing null reopens the window. That looks like
 * rewriting history and is not: nothing was billed under the closure, because closing a window makes
 * a code unbillable for NEW service dates and leaves every past one resolving exactly as before.
 * What an org gets back is the ability to bill the code again — which is the whole reason the action
 * is offered, since "our funder changed their mind" is more common than the mistake it looks like.
 *
 * A DATE IS NOT REQUIRED TO BE IN THE FUTURE. An org told in March that a code stopped being accepted
 * in January is recording a fact, not scheduling one, and refusing the backdated case would leave
 * them no way to say it.
 *
 * @param {{ serviceCodeId: string, clinicId: string, retiredOn: string|null }} request
 * @param {import('../ports/ServiceCodeRepository.port').ServiceCodeRepository} repository
 * @returns {Promise<void>}
 * @throws {ServiceCodeChangeRejected} the code is missing, managed, or already removed
 * @throws {TypeError} `retiredOn` is neither null nor a 'YYYY-MM-DD' calendar date
 */
export async function retireServiceCode({ serviceCodeId, clinicId, retiredOn }, repository) {
  const serviceCode = await findCode(serviceCodeId, clinicId, repository);
  // Null is a real value here — it un-retires — so it is distinguished from an unreadable date
  // rather than folded in with it. Defaulting an unreadable one to null would silently reopen a
  // window the user meant to close.
  const closesOn = retiredOn === null ? null : toAsOfDate(retiredOn);

  if (retiredOn !== null && closesOn === null) {
    throw new TypeError(
      `A retirement date must be 'YYYY-MM-DD' or null; got ${JSON.stringify(retiredOn)}.`,
    );
  }
  // The DATE is part of what the rule judges, so it is read before the rule runs rather than after:
  // a retirement before the code starts closes a window that never opened, and the code then bills
  // on no date at all without anything throwing.
  const rejection = serviceCodeRetirementRejection(serviceCode, closesOn);

  if (rejection !== null) {
    throw new ServiceCodeChangeRejected(rejection, serviceCodeId);
  }

  await repository.retire({ serviceCodeId, retiredOn: closesOn });
} // retireServiceCode()

/**
 * Disowns a code that should never have existed — a tombstone, never a hard delete.
 *
 * THE GATE IS THE DOMAIN RULE'S, and it is deliberately narrow: removable only while the code was
 * minted in this editing session and nothing committed has referenced it. See
 * `serviceCodeRemovalRejection` for why the wider "never referenced or billed" rule is not what
 * ships, and why the narrowing is the safe direction rather than the convenient one.
 *
 * THE EVIDENCE COMES FROM TWO PLACES, and the split is about who can see what. Whether a code was
 * minted in this session is editor state that exists nowhere else, and whether a saved SHEET names
 * it is a question about a charge master the caller already holds — both arrive as arguments.
 * Whether a TREATMENT SERVICE names it is the half nobody on that screen can answer, so it is read
 * here.
 *
 * READING IT HERE RATHER THAN TRUSTING THE CALLER is the point. `hasCommittedReference` used to mean
 * "any sheet mentions it" while being named as though it meant "anything references it" — safe only
 * because the treatment-service picker narrows to codes on a saved sheet, which is a coupling in a
 * different file. Now the name is true.
 *
 * IT FAILS CLOSED. A reference check that cannot run is not evidence of absence — that is the defect
 * AGENTS.md records, where a usage check resolved zero references and reported an in-use record as
 * safe to retire. So a rejected read counts AS a reference: a refused removal is visible and
 * recoverable, an orphaned reference is neither.
 *
 * @param {{ serviceCodeId: string, clinicId: string, isSessionMinted: boolean,
 *   hasChargeMasterReference: boolean }} request
 * @param {import('../ports/ServiceCodeRepository.port').ServiceCodeRepository} repository
 * @returns {Promise<void>}
 * @throws {ServiceCodeChangeRejected} the code is missing, managed, already removed, or referenced
 */
export async function removeServiceCode({
  serviceCodeId,
  clinicId,
  isSessionMinted,
  hasChargeMasterReference,
}, repository) {
  const serviceCode = await findCode(serviceCodeId, clinicId, repository);
  const referencedByService = await repository
    .hasTreatmentServiceReference({ clinicId, serviceCodeId })
    .catch((error) => {
      console.error('Could not check treatment services for a reference; refusing removal', error);

      return true;
    });
  const rejection = serviceCodeRemovalRejection({
    serviceCode,
    isSessionMinted,
    hasCommittedReference: hasChargeMasterReference === true || referencedByService === true,
  });

  if (rejection !== null) {
    throw new ServiceCodeChangeRejected(rejection, serviceCodeId);
  }

  await repository.remove({ serviceCodeId });
} // removeServiceCode()
